import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { AppApis } from '@app/core/APIs/app-apis';
import { WorkflowDraftConfirmationService } from '@app/platform-admin/workflow-registry-diagnostics/workflow-draft-confirmation.service';
import { mapWorkflowRegistryDiagnosticError } from '@app/platform-admin/workflow-registry-diagnostics/platform-workflow-registry-diagnostics.models';
import { UiButtonComponent, UiFormComponent } from '@template-ui/components';
import { InputConfig } from '@template-ui/types/ez-form.types';
import { firstValueFrom } from 'rxjs';

export interface WorkflowBusinessStatusRead {
  code: string;
  label: string;
  choices: { code: string; label: string }[];
  allowManualChanges: boolean;
  canChange: boolean;
  changedUtc: string | null;
  eTag: string;
}

interface WorkflowBusinessStatusReceipt {
  scope: 'workflow' | 'step';
  occurrenceId: number | null;
  statusCode: string;
  label: string;
  eTag: string;
}

@Component({
  selector: 'phms-workflow-business-status',
  standalone: true,
  imports: [CommonModule, UiButtonComponent, UiFormComponent],
  templateUrl: './workflow-business-status.component.html',
  styleUrl: './workflow-business-status.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowBusinessStatusComponent implements OnDestroy {
  readonly workflowId = input.required<number>();
  readonly occurrenceId = input<number | null>(null);
  readonly scope = input<'workflow' | 'step'>('workflow');
  readonly status = input<WorkflowBusinessStatusRead | null>(null);
  readonly preview = input(false);
  readonly isCurrent = input(false);
  readonly disabled = input(false);
  readonly disabledReason = input('');
  readonly reloadRequested = output<void>();
  readonly operationInProgressChange = output<boolean>();

  readonly selectedCode = signal('');
  readonly busy = signal(false);
  readonly confirming = signal(false);
  readonly refreshRequired = signal(false);
  readonly errorMessages = signal<string[]>([]);
  readonly supportReference = signal<string | null>(null);

  private readonly http = inject(HttpClient);
  private readonly apis = inject(AppApis);
  private readonly confirmation = inject(WorkflowDraftConfirmationService);
  private generation = 0;
  private lastOperationState: boolean | null = null;
  private intent: { signature: string; key: string } | null = null;

  readonly title = computed(() =>
    this.scope() === 'workflow' ? 'Workflow status' : 'Step label',
  );
  readonly manualChangesAllowed = computed(() => {
    const status = this.status();
    return (
      !!status?.allowManualChanges &&
      !!status.canChange &&
      this.isCurrent() &&
      !this.preview() &&
      Number.isSafeInteger(this.workflowId()) &&
      this.workflowId() > 0 &&
      Number.isSafeInteger(this.occurrenceId()) &&
      (this.occurrenceId() ?? 0) > 0 &&
      !!status.eTag &&
      status.choices.length > 0
    );
  });
  readonly controlsDisabled = computed(
    () =>
      this.disabled() ||
      this.busy() ||
      this.confirming() ||
      this.refreshRequired(),
  );
  readonly selectedChoice = computed(() =>
    this.status()?.choices.find(
      (choice) => choice.code === this.selectedCode(),
    ),
  );
  readonly canChange = computed(
    () =>
      this.manualChangesAllowed() &&
      !this.controlsDisabled() &&
      !!this.selectedChoice() &&
      this.selectedCode() !== this.status()?.code,
  );
  readonly pickerConfig = computed<InputConfig[]>(() => [
    {
      name: 'status',
      type: 'select',
      label: 'Change to',
      placeholder: 'Choose a status',
      width: 'fill',
      options: (this.status()?.choices ?? []).map((choice) => ({
        value: choice.code,
        viewValue: choice.label,
      })),
      validation: [Validators.required],
    },
  ]);
  readonly pickerValue = computed(() => ({ status: this.selectedCode() }));
  readonly readOnlyHint = computed(() => {
    if (this.preview()) return 'Preview only.';
    if (!this.isCurrent()) return 'Read-only while viewing a past visit.';
    if (!this.status()?.allowManualChanges)
      return 'Updated automatically by this process.';
    return 'Status changes are unavailable for this run.';
  });

  constructor() {
    effect(() => this.emitOperationState(this.busy() || this.confirming()));
    effect(() => {
      const status = this.status();
      this.workflowId();
      this.occurrenceId();
      this.scope();
      this.generation += 1;
      this.intent = null;
      this.selectedCode.set(status?.code ?? '');
      this.refreshRequired.set(false);
      this.errorMessages.set([]);
      this.supportReference.set(null);
    });
  }

  ngOnDestroy(): void {
    this.generation += 1;
    this.emitOperationState(false);
  }

  select(value: Record<string, unknown>): void {
    if (!this.manualChangesAllowed() || this.controlsDisabled()) return;
    if (typeof value['status'] === 'string')
      this.selectedCode.set(value['status']);
  }

  async changeStatus(): Promise<void> {
    if (!this.canChange()) return;
    const generation = this.generation;
    const signature = this.intentSignature();
    const status = this.status();
    const choice = this.selectedChoice();
    if (!status || !choice) return;
    this.setOperationState(true, false);
    this.errorMessages.set([]);
    this.supportReference.set(null);
    let confirmed = false;
    try {
      confirmed = await this.confirmation.confirm({
        heading: `Change ${this.title().toLowerCase()}?`,
        message: `Set the ${this.scope() === 'workflow' ? 'process' : 'step'} status to “${choice.label}”?`,
        confirmText: 'Change status',
        cancelText: 'Keep current status',
        tone: 'info',
      });
    } catch {
      if (generation === this.generation)
        this.errorMessages.set([
          'The status review could not be opened. Try again.',
        ]);
      this.setOperationState(false, false);
      return;
    }
    if (
      !confirmed ||
      generation !== this.generation ||
      signature !== this.intentSignature() ||
      !this.manualChangesAllowed() ||
      this.disabled() ||
      this.busy() ||
      this.refreshRequired() ||
      !this.selectedChoice() ||
      this.selectedCode() === this.status()?.code
    ) {
      this.setOperationState(false, false);
      return;
    }
    const scope = this.scope();
    const statusCode = choice.code;
    this.setOperationState(false, true);
    try {
      if (this.intent?.signature !== signature)
        this.intent = { signature, key: crypto.randomUUID() };
      const result = await firstValueFrom(
        this.http.post<WorkflowBusinessStatusReceipt>(
          this.apis.core.manual.platformWorkflowExecution(
            this.workflowId(),
            '/status',
          ),
          {
            scope,
            occurrenceId: this.occurrenceId(),
            statusCode,
            expectedETag: status.eTag,
          },
          {
            withCredentials: true,
            headers: new HttpHeaders({
              'skip-error-interceptor': 'yes',
              'skip-loading-interceptor': 'yes',
              'Idempotency-Key': this.intent.key,
            }),
          },
        ),
      );
      if (generation !== this.generation) return;
      if (
        !result ||
        result.scope !== scope ||
        result.occurrenceId !== this.occurrenceId() ||
        result.statusCode !== statusCode ||
        typeof result.eTag !== 'string' ||
        !result.eTag.trim()
      ) {
        this.unconfirmedOutcome();
        return;
      }
      this.refreshRequired.set(true);
      this.setOperationState(false, false);
      this.reloadRequested.emit();
    } catch (error) {
      if (generation !== this.generation) return;
      if (
        error instanceof HttpErrorResponse &&
        [400, 409, 412].includes(error.status)
      ) {
        const mapped = mapWorkflowRegistryDiagnosticError(error);
        this.errorMessages.set(mapped.messages);
        this.supportReference.set(mapped.correlationId || null);
        if (error.status !== 400) this.refreshRequired.set(true);
      } else if (
        error instanceof HttpErrorResponse &&
        [401, 403, 404].includes(error.status)
      ) {
        this.errorMessages.set([
          'Your access to change this status could not be confirmed. Refresh the process to continue.',
        ]);
        this.refreshRequired.set(true);
      } else {
        this.unconfirmedOutcome();
        if (error instanceof HttpErrorResponse)
          this.supportReference.set(
            mapWorkflowRegistryDiagnosticError(error).correlationId || null,
          );
      }
    } finally {
      this.setOperationState(false, false);
    }
  }

  refresh(): void {
    if (this.preview() || this.busy() || this.confirming() || this.disabled())
      return;
    this.reloadRequested.emit();
  }

  private intentSignature(): string {
    return JSON.stringify([
      this.workflowId(),
      this.occurrenceId(),
      this.scope(),
      this.selectedCode(),
      this.status()?.eTag,
    ]);
  }

  private emitOperationState(inProgress: boolean): void {
    if (this.lastOperationState === inProgress) return;
    this.lastOperationState = inProgress;
    this.operationInProgressChange.emit(inProgress);
  }

  private setOperationState(confirming: boolean, busy: boolean): void {
    this.confirming.set(confirming);
    this.busy.set(busy);
    this.emitOperationState(confirming || busy);
  }

  private unconfirmedOutcome(): void {
    this.errorMessages.set([
      'The status change was not confirmed. Refresh to check the current status before making another change.',
    ]);
    this.refreshRequired.set(true);
  }
}
